export const bridgeRules = [
  {
    "id": "HF-01",
    "trigger": "Confirmed HF and new/worsening congestion or recent decompensation with unresolved plan",
    "action": "Reassess volume status, precipitating factors and monitoring plan; verify access to prescribed therapy",
    "guard": "Active symptoms use approved triage first. No automatic diuretic increase; retrieve discharge orders/labs",
    "group": "Heart failure"
  },
  {
    "id": "HF-02",
    "trigger": "Confirmed HFrEF and reconciled therapy lacks a core class without a documented explanation",
    "action": "Review eligibility/tolerance for ARNI, evidence-based beta-blocker, MRA and SGLT2 inhibitor; document each omission",
    "guard": "Unknown list/phenotype \u2192 verify; contraindication \u2192 record. Initiation/titration requires clinical stability and safety assessment",
    "group": "Heart failure"
  },
  {
    "id": "HF-03",
    "trigger": "Confirmed HFpEF and disease-modifying strategy is absent or needs reconsideration",
    "action": "Review against 2026 ACC HFpEF pathway, including SGLT2/nonsteroidal-MRA eligibility and comorbidity treatment",
    "guard": "Full-text eligibility/monitoring extraction remains required before medication-specific automation; do not substitute HFrEF checklist",
    "group": "Heart failure"
  },
  {
    "id": "HF-04",
    "trigger": "Persistent high-risk HF despite an established plan, or complex treatment intolerance",
    "action": "Consider HF-specialist review and coordinated care; address symptom goals",
    "guard": "Do not infer advanced HF from admission count alone",
    "group": "Heart failure"
  },
  {
    "id": "COPD-01",
    "trigger": "COPD label without accessible confirming testing",
    "action": "Retrieve spirometry; ask provider to confirm diagnosis",
    "guard": "Do not remove a diagnosis or interrupt therapy because the download is incomplete",
    "group": "COPD"
  },
  {
    "id": "COPD-02",
    "trigger": "Confirmed COPD, persistent moderate/severe symptoms and monotherapy",
    "action": "Review LABA/LAMA suitability after checking actual use/device barriers",
    "guard": "Do not select drugs from symptom text alone; rule is provider review",
    "group": "COPD"
  },
  {
    "id": "COPD-03",
    "trigger": "COPD exacerbation treated with systemic steroids/antibiotics in past year, persistent management gap",
    "action": "Review exacerbation prevention and triple-therapy suitability",
    "guard": "Assess asthma, eosinophils, pneumonia history, prior adverse effects and actual therapy; no automatic ICS change",
    "group": "COPD"
  },
  {
    "id": "COPD-04",
    "trigger": "Hospitalization for COPD exacerbation and no documented rehabilitation disposition",
    "action": "Offer provider review for pulmonary rehabilitation and access",
    "guard": "Verify clinical suitability, preferences and existing referral; track participation, not just order",
    "group": "COPD"
  },
  {
    "id": "DM-01",
    "trigger": "Documented glucose <54 mg/dL OR hypoglycemic event requiring another person's assistance",
    "action": "Review regimen for hypoglycemia reduction; consider deintensification/switch and prevention plan",
    "guard": "Active episode goes to approved immediate response; historical event does not itself prescribe disposition",
    "group": "Diabetes"
  },
  {
    "id": "DM-02",
    "trigger": "Older adult's regimen exceeds demonstrated self-management capacity or treatment burden outweighs benefit",
    "action": "Review simplification and individualized goals",
    "guard": "Do not infer incapacity from age; include support and preferences",
    "group": "Diabetes"
  },
  {
    "id": "DM-03",
    "trigger": "Repeated readings above the patient's documented target with a verified treatment/access gap",
    "action": "Review cause and plan; distinguish access failure, acute illness and therapy inadequacy",
    "guard": "No universal A1C target or automatic insulin escalation; absent target \u2192 provider goal clarification",
    "group": "Diabetes"
  },
  {
    "id": "DM-04",
    "trigger": "Prescribed monitoring cannot be performed because equipment/access is missing",
    "action": "Resolve meter/strips or prescribed-device access and education",
    "guard": "Confirm the actual monitoring prescription; do not require routine fingersticks for every patient",
    "group": "Diabetes"
  },
  {
    "id": "REN-01",
    "trigger": "Confirmed CKD and subsequent eGFR change >20%, or ACR doubles",
    "action": "Request evaluation of change and cause; acute illness/medication context matters",
    "guard": "Verify dates, units and acute illness or medication context. This is an evaluation signal, not an automatic medication hold.",
    "group": "Kidney disease"
  },
  {
    "id": "REN-02",
    "trigger": "CKD G3\u2013G5 with valid inputs for a validated kidney-failure equation",
    "action": "Report risk with provenance. Five-year risk 3\u20135% supports referral consideration; >10% two-year risk supports multidisciplinary review",
    "guard": "Require valid inputs and applicable CKD G3-G5 population. Do not calculate from AKI. Preserve the 3-5% referral range for clinician interpretation; do not invent a fixed cutoff.",
    "group": "Kidney disease"
  },
  {
    "id": "REN-03",
    "trigger": "AKI/recent kidney-affecting medication change and unresolved ordered monitoring/follow-up",
    "action": "Retrieve orders and ask clinician to specify missing monitoring; no generic medication hold",
    "guard": "Retrieve current orders and recovery evidence. The clinician specifies monitoring; no generic medication hold or blanket laboratory interval.",
    "group": "Kidney disease"
  },
  {
    "id": "FUNC-01",
    "trigger": "Community-dwelling adult \u226565 reports fall in past year, unsteadiness or fear of falling",
    "action": "Request multifactorial fall assessment; identify modifiable factors and targeted PT/home-safety/medication review",
    "guard": "Current injury/acute symptoms first; silence in notes is UNKNOWN",
    "group": "Function & cognition"
  },
  {
    "id": "COG-01",
    "trigger": "New cognitive change or distress with an unresolved cause",
    "action": "Request clinical evaluation of reversible causes, medication effects and environment",
    "guard": "Acute confusion uses urgent assessment; do not label progression or recommend sedatives from a note alone",
    "group": "Function & cognition"
  },
  {
    "id": "FUNC-02",
    "trigger": "New ADL/IADL loss or caregiver inability to execute the plan",
    "action": "Review rehabilitation, equipment, home support and care-plan feasibility",
    "guard": "Pear coordination predicate; not a validated frailty score or hospice test",
    "group": "Function & cognition"
  },
  {
    "id": "PAL-01",
    "trigger": "Persistent burdensome symptoms, caregiver distress or difficult treatment choices despite a plan",
    "action": "Ask provider to assess palliative/supportive needs alongside disease treatment",
    "guard": "Level is not the sole eligibility gate; document patient goals",
    "group": "Advanced illness"
  },
  {
    "id": "PAL-02",
    "trigger": "Documented advanced progressive illness and unresolved prognosis/goals questions",
    "action": "Offer clinician-led serious-illness discussion and consider hospice eligibility assessment when appropriate",
    "guard": "No automatic referral, prognosis or treatment withdrawal from Level 6",
    "group": "Advanced illness"
  }
]
